import React, { useState } from 'react';
import {
  Form, Input, Select, Button, Alert, message, Typography, Space,
} from 'antd';
import { useSelector } from 'react-redux';
import { useBroadcastNotificationMutation } from '../services/adminApi';
import AdminPageHeader, { adminPanelStyle } from './AdminPageHeader';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

const AUDIENCE_OPTIONS = [
  { value: 'all', label: 'All users' },
  { value: 'admins', label: 'Admins only' },
  { value: 'moderators', label: 'Admins + moderators' },
  { value: 'contributors', label: 'Contributors and above' },
];

export default function NotificationsPage() {
  const { user } = useSelector((state) => state.auth);
  const isAdmin = user?.role === 'admin';
  const [broadcast, { isLoading }] = useBroadcastNotificationMutation();
  const [form] = Form.useForm();
  const [lastResult, setLastResult] = useState(null);

  const handleSubmit = async (values) => {
    try {
      const result = await broadcast(values).unwrap();
      setLastResult(result);
      message.success(`Created ${result.created} in-app notification(s)`);
      form.resetFields();
      form.setFieldsValue({ audience: 'all', type: 'system' });
    } catch (err) {
      message.error(err.data?.title || 'Broadcast failed');
    }
  };

  return (
    <div>
      <AdminPageHeader
        title="Notifications"
        subtitle="Broadcast an in-app system notification to selected audiences"
      />

      {!isAdmin && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Broadcast requires the admin role. Moderators can view this page but cannot send."
        />
      )}

      <div style={{ ...adminPanelStyle, padding: 20, maxWidth: 640 }}>
        <Paragraph style={{ color: 'var(--df-text-secondary)', marginTop: 0 }}>
          Creates real rows in the
          {' '}
          <Text code>notifications</Text>
          {' '}
          table (in-app channel). Email / push delivery is controlled by the
          {' '}
          <Text code>email_notifications</Text>
          {' '}
          feature flag and is not faked here.
        </Paragraph>

        <Form
          form={form}
          layout="vertical"
          disabled={!isAdmin}
          initialValues={{ audience: 'all', type: 'system' }}
          onFinish={handleSubmit}
        >
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, min: 2, message: 'Title is required' }]}
          >
            <Input placeholder="Maintenance window tonight" maxLength={500} />
          </Form.Item>
          <Form.Item name="body" label="Body">
            <TextArea rows={4} placeholder="Optional details shown in the user’s notification center" maxLength={5000} />
          </Form.Item>
          <Form.Item name="audience" label="Audience" rules={[{ required: true }]}>
            <Select options={AUDIENCE_OPTIONS} />
          </Form.Item>
          <Form.Item name="type" label="Type">
            <Input placeholder="system" />
          </Form.Item>
          <Form.Item name="actionUrl" label="Action URL">
            <Input placeholder="/deals or https://…" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={isLoading} disabled={!isAdmin}>
                Send in-app broadcast
              </Button>
            </Space>
          </Form.Item>
        </Form>

        {lastResult && (
          <Alert
            type="success"
            showIcon
            message={`Last send: ${lastResult.created} notification(s) to audience “${lastResult.audience}”`}
          />
        )}
      </div>
    </div>
  );
}
